import React, { useCallback, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'

import Icon from '../../../atoms/icon/Icon'
import HoverableIcon from '../../../atoms/icon/HoverableIcon'
import Table from '../../../atoms/table/Table'

import { shortDate } from '../../../../lib/date'
import Clickable from '../../../atoms/clickable/Clickable'
import ToggleEvent from '../edit/ToggleEvent'
import DeleteEvent from '../edit/DeleteEvent'
import StickyBar from '../../../organisms/sticky-bar/StickyBar'
import EmptyList from '../../../organisms/empty-list/EmptyList'
import IconButton from '../../../molecules/iconbutton/IconButton'

interface Props {
  events: API.EventResponseSummary[]
  refreshEvents: () => void
}

const getStreamName = (event: API.EventResponseSummary) => {
  let result = '-'
  if (event.streamName) {
    result = event.streamName
  }
  return result
}

interface EventState {
  eventModalOpen: boolean
  deleteModalOpen: boolean
  eventSelected: API.EventResponseSummary | null
}

const STICKY_BAR_HEIGHT = 8

function EventsTable({ events, refreshEvents }: Props) {
  const [{ eventModalOpen, deleteModalOpen, eventSelected }, setEventState] = useState<EventState>({ eventModalOpen: false, deleteModalOpen: false, eventSelected: null })
  const history = useHistory()

  const onEventToggleComplete = useCallback(() => {
    setEventState(prevState => ({ ...prevState, eventModalOpen: false, eventSelected: null }))
    refreshEvents()
  }, [refreshEvents])

  const onEventDeleteComplete = useCallback(() => {
    setEventState(prevState => ({ ...prevState, deleteModalOpen: false, eventSelected: null }))
    refreshEvents()
  }, [refreshEvents])

  return (
    <>
      {eventModalOpen && !!eventSelected && <ToggleEvent
        isOpen={eventModalOpen}
        onClose={() => setEventState(prevState => ({ ...prevState, eventModalOpen: false, eventSelected: null }))}
        onComplete={onEventToggleComplete}
        event={eventSelected}
      />}
      {deleteModalOpen && !!eventSelected && <DeleteEvent
        isOpen={deleteModalOpen}
        onClose={() => setEventState(prevState => ({ ...prevState, deleteModalOpen: false, eventSelected: null }))}
        onComplete={onEventDeleteComplete}
        event={eventSelected}
      />}
      <StickyBar height={STICKY_BAR_HEIGHT}>
        <Link data-test='add-event' to='/events/add'>
          <IconButton icon='addEvent' text='Add Event' />
        </Link>
      </StickyBar>
      {!events.length && <EmptyList icon='events' message='There are no events yet' />}
      {!!events.length &&
        <Table stickyHeader stickyTop={STICKY_BAR_HEIGHT} clickable>
          <thead>
            <tr>
              <th>Event Date</th>
              <th>Client</th>
              <th>Event Name</th>
              <th>Stream</th>
              <th>Participants #</th>
              <th className='right'>Actions</th>
            </tr>
          </thead>
          <tbody>
            {events.map(e => (
              <tr className='eventsTable-row' key={e.id} data-test={e.id} onClick={(event) => {
                event.stopPropagation()
                event.preventDefault()
                if (process.env.REACT_APP_USE_NEW_EVENT_UI === 'true') {
                  history.push(`/events/${e.id}/view-wizard`)
                } else {
                  history.push(`/events/${e.id}/view`)
                }
              }}>
                <td>{shortDate(e.date)}</td>
                <td>{e.client}</td>
                <td>{e.name}</td>
                <td>{getStreamName(e)}</td>
                <td>{e.participantsCount}</td>
                <td className='right'>
                  <Icon type='search' tip={{ content: 'View Event', id: `${e.id}-view` }} />
                  <Clickable
                    data-test={`clone-${e.id}`}
                    onClick={(event) => {
                      event.stopPropagation()
                      history.push(`/events/${e.id}/clone`)
                    }}>
                    <Icon type='copy' tip={{
                      content: (
                        <>
                          <b>Clone Event</b>
                          <p>Create a new event using the details <br /> of this event as a starting point.</p>
                        </>
                      ), id: `${e.id}-copy`
                    }} />
                  </Clickable>
                  <Clickable
                    data-test={`toggle-${e.id}`}
                    onClick={(event) => {
                      event.stopPropagation()
                      setEventState({ eventModalOpen: true, deleteModalOpen: false, eventSelected: e })
                    }}>
                    <HoverableIcon
                      hoverType={e.closed ? 'unlock' : 'lock'}
                      type={e.closed ? 'lock' : 'unlock'}
                      tip={{
                        id: `${e.id}-toggle`,
                        content: e.closed ? (
                          <>
                            <b>Open Event</b>
                            <p>This will re-open the event and it <br />
                              will be accessible to participants.</p>
                          </>
                        ) : (
                          <>
                            <b>Close Event</b>
                            <p>This will close the event. Participants <br />
                              will no longer be able to access the event.</p>
                          </>
                        )
                      }}
                    />
                  </Clickable>
                  {e.closed && <Clickable
                    data-test={`delete-${e.id}`}
                    onClick={(event) => {
                      event.stopPropagation()
                      setEventState({ eventModalOpen: false, deleteModalOpen: true, eventSelected: e })
                    }}>
                    <Icon
                      type='trash'
                      tip={{
                        content: 'Delete event',
                        id: `${e.id}-delete`
                      }}
                    />
                  </Clickable>}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      }
    </>
  )
}

export default EventsTable
